/**
 * Emoji reaction bar.
 *
 * The allowed emojis come from the server (reaction_types table) rather
 * than being hardcoded here, so admins can enable/disable them without a
 * deploy. We fetch the list once at module level via the React cache below.
 *
 * Props:
 *   counts           dict of emoji -> count for this target
 *   myReactions      array of emojis the viewer has already reacted with
 *   isAuthenticated  bool — gates the click handler
 *   onToggle         async (emoji) => void; parent performs the API call
 *                    and updates counts/myReactions on its side
 */

import { useEffect, useState } from 'react';

import { listReactionTypes } from '../../api/tracks.js';


// Shared across all ReactionBar instances; one network request per page load.
let _typesPromise = null;
function loadReactionTypes() {
  if (!_typesPromise) _typesPromise = listReactionTypes();
  return _typesPromise;
}


export default function ReactionBar({
  counts = {},
  myReactions = [],
  isAuthenticated,
  onToggle,
}) {
  const [types, setTypes] = useState(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadReactionTypes()
      .then((data) => {
        if (!cancelled) setTypes(data || []);
      })
      .catch(() => {
        if (!cancelled) setTypes([]);
      });
    return () => { cancelled = true; };
  }, []);

  async function handleClick(emoji) {
    if (!isAuthenticated || pending) return;
    setPending(emoji);
    try {
      await onToggle(emoji);
    } finally {
      setPending(null);
    }
  }

  if (!types) return null;  // brief flicker on first paint; acceptable

  return (
    <div className="tt-reactions" role="group" aria-label="Reactions">
      {types.map(({ emoji, label }) => {
        const count = counts[emoji] || 0;
        const mine = myReactions.includes(emoji);
        const isPending = pending === emoji;
        return (
          <button
            key={emoji}
            type="button"
            className={[
              'tt-reaction',
              mine ? 'is-mine' : '',
              count > 0 ? 'has-count' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => handleClick(emoji)}
            disabled={!isAuthenticated || isPending}
            title={
              !isAuthenticated
                ? 'Sign in to react'
                : mine
                  ? `Remove ${label || emoji}`
                  : label || 'React'
            }
            aria-pressed={mine}
          >
            <span className="tt-reaction__emoji">{emoji}</span>
            {count > 0 && <span className="tt-reaction__count">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
